import { Form, Formik } from "formik";

import moment from "moment";
import { useState } from "react";
import { PiMapPinLineFill } from "react-icons/pi";
import * as yup from "yup";

import { ERROR_MSG } from "../../constants/common";
import {
    RESET_LOADER,
    SET_ERROR,
    SET_LOADER,
} from "../../constants/contexConstant";
import {
    emailRegEx,
    nameRegEx,
    phoneRegEx,
} from "../../constants/regularExpression";
import { NEW_ENQUIRY_ENDPOINT } from "../../constants/routes";
import { useContextState } from "../../context/ContextProvider";
import {
    getCleanedAddressFromAutoComplete,
    validateEmail,
    validatePhone,
} from "../../helpers/helpers";
import { debounce } from "../../lib";
import cn from "../../lib/cn";
import { check } from "../../resources/js/images";
import request from "../../services/request";
import Container from "../Container";
import GoogleMap from "../map";
import SiteAddress from "../map/SiteAddress";
import P from "../typography/P";
import Button from "../ui/Button";
import DrawerAndModalWrapper from "../ui/Drawer/DrawerAndModalWrapper";
import DateTimePickerBlockFormik from "../ui/Form/DateTimePickerBlockFormik";
import FormikLabeledInput from "../ui/Form/FormikLabeledInput";
import {
    addressCityStatePinCodeSchemaShape,
    enquiryNameEmailPhoneAndBillSchema,
} from "../ui/Form/schema";
import Stepper from "../ui/Stepper";

const center = {
    lat: 18.5204303,
    lng: 73.8567437,
};
const EnquiryInternalUser = ({ isOpen, onClose }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const { dispatch } = useContextState();
    const [existingEmail, setExistingEmail] = useState(false);
    const [existingPhone, setExistingPhone] = useState(false);
    const [position, setPosition] = useState(center);

    const [initialValues, setInitialValues] = useState({
        name: "",
        email: "",
        phone: "",
        billAmount: "",
        date: new Date(),
        hour: 10,
        minutes: 0,
        projectName: "",
        city: "",
        address: "",
        pincode: "",
        state: "",
        mapAddress: "",
        lat: center.lat,
        lng: center.lng,
    });

    const handlePlaceChange = async (place, siteRef, setFieldValue) => {
        const suggestion = siteRef;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setPosition({ lat, lng });

        const { city, state, cleanedAddress, pincode } =
            await getCleanedAddressFromAutoComplete(
                place,
                suggestion,
                lat,
                lng
            );

        setFieldValue("address", cleanedAddress);
        setFieldValue("city", city);
        setFieldValue("state", state);
        setFieldValue("pincode", pincode);
        setFieldValue("lat", lat);
        setFieldValue("lng", lng);
    };

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 13);

    const getValidationSchema = () => {
        if (currentStep === 1) return enquiryNameEmailPhoneAndBillSchema;
        else if (currentStep === 2)
            return yup.object().shape({
                address: yup.string().required().label("Address"),
                projectName: yup
                    .string()
                    .required()
                    .min(3)
                    .max(50)
                    .matches(
                        nameRegEx,
                        "Name should start with letter and not contain any special characters or numbers"
                    )
                    .label("Project Name"),
            });
        else {
            return yup.object().shape({
                ...{
                    address: yup.string().required().label("Address"),
                    ...addressCityStatePinCodeSchemaShape,
                },
            });
        }
    };

    const handleStep = (values, actions) => {
        setInitialValues(values);
        actions.setSubmitting(false);
        if (currentStep === 1) {
            setCurrentStep(2);
        } else if (currentStep === 2) {
            setCurrentStep(3);
            actions.setSubmitting(false);
        } else {
            handleCompleteEnquiry(values, actions);
            actions.setSubmitting(true);
        }
    };

    const handleCompleteEnquiry = async (values, actions) => {
        try {
            let hoursInSecond = values.hour * 60 * 60;
            let minutes = values.minutes * 60;
            let reqData = {
                date: `${
                    moment(values.date).startOf("day").unix() +
                    hoursInSecond +
                    minutes
                }`,
                bill: values.billAmount,
                phone: `91${values.phone}`,
                name: values.name,
                email: values.email,
                city: values.city,
                state: values.state,
                pinCode: values.pincode,
                address: values.address,
                latitude: values.lat,
                longitude: values.lng,
                propertyName: values.projectName,
            };

            dispatch({ type: SET_LOADER });
            await request.authPost({
                endpoint: NEW_ENQUIRY_ENDPOINT,
                body: reqData,
            });

            actions.resetForm();
            dispatch({ type: RESET_LOADER });
            actions.setSubmitting(false);
            setCurrentStep(4);
        } catch (error) {
            dispatch({ type: RESET_LOADER });
            actions.setSubmitting(false);
            dispatch({
                type: SET_ERROR,
                payload: typeof error === "string" ? error : ERROR_MSG,
            });
        }
    };

    const handleCloseDrawerForm = (resetForm) => {
        setCurrentStep(1);
        resetForm();
        onClose();
    };

    const handleValidateCred = debounce(async (value, name) => {
        try {
            if (name === "phone") {
                if (phoneRegEx.test(value)) {
                    await validatePhone(value);
                    setExistingPhone("");
                }
            } else if (name === "email") {
                if (!emailRegEx.test(value)) return;
                await validateEmail(value);
                setExistingEmail("");
            }
        } catch (error) {
            if (name === "email") setExistingEmail(error);
            else if (name === "phone") setExistingPhone(error);
        }
    }, 500);

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleStep}
            validationSchema={getValidationSchema()}
            validateOnBlur={false}
        >
            {({
                isValid,
                isSubmitting,
                dirty,
                errors,
                setFieldValue,

                validateField,

                setFieldError,
                resetForm,

                values,
            }) => (
                <Form>
                    <DrawerAndModalWrapper
                        isOpen={isOpen}
                        onClose={() => {
                            setExistingEmail("");
                            setExistingPhone("");
                            handleCloseDrawerForm(resetForm);
                        }}
                        drawerClassName="z-[99]"
                        drawerContentClassName="bg-white min-h-[95vh]"
                        modalContentClass="bg-white"
                    >
                        <div className=" bg-white">
                            <div className="rounded-t-4 pb-7 lg:pb-0">
                                <Container>
                                    <div className="flex-1 flex flex-col items-center h-full">
                                        <div className="w-[80%] lg:w-5/6 mb-2 lg:mb-0">
                                            <Stepper
                                                steps={steps}
                                                currentStep={currentStep}
                                                onClick={(step) =>
                                                    step < currentStep &&
                                                    setCurrentStep(step)
                                                }
                                                theme={"light"}
                                                showCurrentStep={false}
                                            />
                                        </div>
                                    </div>

                                    {currentStep < 4 ? (
                                        <div>
                                            <div
                                                className={cn(
                                                    "flex flex-col lg:grid lg:grid-cols-2 gap-x-2",
                                                    {
                                                        "hidden lg:hidden":
                                                            currentStep !== 1,
                                                    }
                                                )}
                                            >
                                                <FormikLabeledInput
                                                    label="Full Name"
                                                    placeholder="Enter Full Name"
                                                    required
                                                    name="name"
                                                />
                                                <FormikLabeledInput
                                                    label="Email Id"
                                                    placeholder="Enter Email Id"
                                                    name="email"
                                                    required
                                                    autoComplete="email@gmail.com"
                                                    additionalValidate={
                                                        handleValidateCred
                                                    }
                                                    onChange={async (e) => {
                                                        const val =
                                                            e.target.value;
                                                        await setFieldValue(
                                                            "email",
                                                            val
                                                        );
                                                        await validateField(
                                                            "email"
                                                        );

                                                        if (!errors.email) {
                                                            handleValidateCred(
                                                                val,
                                                                "email"
                                                            );
                                                        }
                                                    }}
                                                    errorMessage={existingEmail}
                                                />

                                                <FormikLabeledInput
                                                    label="Phone Number"
                                                    placeholder="Enter Phone Number"
                                                    name="phone"
                                                    autoComplete="9111111111"
                                                    required
                                                    additionalValidate={
                                                        handleValidateCred
                                                    }
                                                    onChange={async (e) => {
                                                        const val =
                                                            e.target.value;
                                                        await setFieldValue(
                                                            "phone",
                                                            val
                                                        );
                                                        let error =
                                                            await validateField(
                                                                "phone"
                                                            );
                                                        if (!error) {
                                                            handleValidateCred(
                                                                val,
                                                                "phone"
                                                            );
                                                        }
                                                    }}
                                                    errorMessage={existingPhone}
                                                />
                                                <FormikLabeledInput
                                                    placeholder="Enter Bill Amount"
                                                    label="Bill Amount"
                                                    name="billAmount"
                                                    required
                                                />
                                            </div>
                                            <div
                                                className={cn(
                                                    "w-full flex flex-col",
                                                    {
                                                        hidden:
                                                            currentStep !== 1,
                                                    }
                                                )}
                                            >
                                                <DateTimePickerBlockFormik
                                                    label="Survey Date"
                                                    dateName="date"
                                                    hourName="hour"
                                                    minuteName="minutes"
                                                    maxDate={
                                                        new Date(
                                                            new Date().getFullYear(),
                                                            new Date().getMonth() +
                                                                1,
                                                            new Date().getDate()
                                                        )
                                                    }
                                                />
                                                <Button
                                                    disabled={
                                                        existingEmail ||
                                                        existingPhone ||
                                                        isSubmitting ||
                                                        !(isValid && dirty)
                                                    }
                                                    className="bg-primary w-full"
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                            <div
                                                className={cn("flex flex-col", {
                                                    hidden: currentStep !== 2,
                                                })}
                                            >
                                                <FormikLabeledInput
                                                    placeholder="Enter Project Name"
                                                    label="Project Name"
                                                    required
                                                    name="projectName"
                                                />
                                                <SiteAddress
                                                    position={position}
                                                    setPosition={setPosition}
                                                    onPlaceSelect={(
                                                        place,
                                                        siteRef
                                                    ) =>
                                                        handlePlaceChange(
                                                            place,
                                                            siteRef,
                                                            setFieldValue
                                                        )
                                                    }
                                                />

                                                {values.address &&
                                                    values.projectName &&
                                                    isValid &&
                                                    dirty && (
                                                        <div className="mt-2">
                                                            <div className="flex items-center gap-1">
                                                                <PiMapPinLineFill
                                                                    size={26}
                                                                    className="text-orange"
                                                                />
                                                                <P className="text-sm text-orange font-vietnam">
                                                                    {
                                                                        values.projectName
                                                                    }
                                                                </P>
                                                            </div>

                                                            <P className="text-xs font-vietnam">
                                                                {values.address}
                                                            </P>
                                                        </div>
                                                    )}

                                                <Button
                                                    type="submit"
                                                    disabled={
                                                        isSubmitting ||
                                                        !(isValid && dirty)
                                                    }
                                                    className="bg-primary w-full mt-2"
                                                >
                                                    Confirm Address
                                                </Button>
                                            </div>

                                            <div
                                                className={cn("flex flex-col", {
                                                    hidden: currentStep !== 3,
                                                })}
                                            >
                                                <div className="grid grid-cols-1 gap-1.6 mt-1.6 lg:gap-x-[3rem] lg:mt-0 ">
                                                    <GoogleMap
                                                        position={position}
                                                        setPosition={
                                                            setPosition
                                                        }
                                                    />
                                                </div>
                                                <p className="text-xs mt-1 font-vietnam">
                                                    Update the address if not
                                                    showing correctly
                                                </p>

                                                <div
                                                    className={cn(
                                                        "grid grid-cols-1 mt-2 "
                                                    )}
                                                >
                                                    <FormikLabeledInput
                                                        placeholder="Street Address"
                                                        label="Street Address"
                                                        required
                                                        name="address"
                                                    />
                                                    <FormikLabeledInput
                                                        placeholder="City"
                                                        label="City"
                                                        required
                                                        name="city"
                                                    />
                                                    <FormikLabeledInput
                                                        placeholder="State"
                                                        label="State"
                                                        required
                                                        name="state"
                                                    />
                                                    <FormikLabeledInput
                                                        placeholder="Pincode"
                                                        label="Pincode"
                                                        required
                                                        name="pincode"
                                                    />
                                                </div>
                                                <Button
                                                    type="submit"
                                                    disabled={
                                                        isSubmitting ||
                                                        !(isValid && dirty)
                                                    }
                                                    className="bg-primary w-full mt-2 lg:mt-0"
                                                >
                                                    Submit Details
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="flex flex-col items-center justify-center ">
                                                <img
                                                    src={check}
                                                    alt="check mark"
                                                    className="w-6 h-6"
                                                />
                                                <P className="font-vietnam xl:text-base mt-2 text-center">
                                                    An email will be sent to
                                                    customer registered email id
                                                </P>
                                            </div>
                                            <Button
                                                className="bg-primary text-white mt-2 w-full "
                                                onClick={() => {
                                                    setCurrentStep(1);
                                                    onClose();
                                                }}
                                            >
                                                Done
                                            </Button>
                                        </div>
                                    )}
                                </Container>
                            </div>
                        </div>
                    </DrawerAndModalWrapper>
                </Form>
            )}
        </Formik>
    );
};

export default EnquiryInternalUser;

let steps = ["Consumer Profile", "Site Location", "Address Confirmation"];
